import * as Sentry from '@sentry/react';
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Dumbbell, ArrowRight, CheckCircle2, Zap, Rocket, ShieldCheck, Mail, Lock, Building, User, Sparkles, PartyPopper, Loader2, ExternalLink } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { gymApi, authApi, adminApi } from '@/lib/api';
import { SaasTier } from '@/types';
import { TermsContent } from '@/components/TermsContent';
import { LEGAL_DOCS } from '@/constants/terms';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';

const FEATURE_LABELS: Record<string, string> = {
    'clients': 'Control de Clientes',
    'memberships': 'Membresías',
    'scanner': 'Scanner QR de Acceso',
    'inventory': 'Inventario',
    'classes': 'Clases',
    'reports': 'Reportes y Analíticas',
    'sales': 'Ventas (POS)',
    'expenses': 'Gestión de Gastos',
    'employees': 'Gestión de Empleados',
};

const FALLBACK_ELITE_MODULES = ['clients', 'memberships', 'scanner', 'inventory', 'classes', 'reports', 'sales', 'expenses', 'employees'];

export default function RegisterGymPage() {
    const [step, setStep] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [isRegistered, setIsRegistered] = useState(false);
    const [checkingEmail, setCheckingEmail] = useState(false);
    const { toast } = useToast();
    const navigate = useNavigate();
    const { refreshAuth } = useAuth();
    const [dbTiers, setDbTiers] = useState<SaasTier[]>([]);
    const [emailError, setEmailError] = useState<string | null>(null);
    const [registredGymName, setRegisteredGymName] = useState('');

    useEffect(() => {
        Sentry.setTag("area", "onboarding");
    }, []);

    useEffect(() => {
        const fetchTiers = async () => {
            try {
                const res = await adminApi.listSaasTiers();
                if (res.success) {
                    const publicTiers = res.tiers.filter((t: SaasTier) => !t.is_private);
                    setDbTiers(publicTiers);
                }
            } catch (e) {
                console.error('Error fetching SaaS Tiers:', e);
            }
        };
        fetchTiers();
    }, []);

    const eliteTrial = dbTiers.find((t: SaasTier) => t.name.toLowerCase().includes('elite') && !t.is_private);
    const eliteTrialName = eliteTrial?.name || 'Elite';
    const eliteTrialPrice = eliteTrial?.price !== undefined ? eliteTrial.price.toString() : '99.99';
    const eliteTrialFeatures = (eliteTrial?.included_modules?.length ? eliteTrial.included_modules : FALLBACK_ELITE_MODULES)
        .map(m => FEATURE_LABELS[m] || m);

    const [formData, setFormData] = useState({
        email: '',
        password: '',
        fullName: '',
        gymName: '',
        acceptTerms: false
    });

    const [showTermsModal, setShowTermsModal] = useState(false);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        if (e.target.name === 'email' && emailError) {
            setEmailError(null);
        }
    };

    const checkEmailExists = useCallback(async (email: string) => {
        if (!email || !email.includes('@')) return;
        setCheckingEmail(true);
        try {
            const { data, error } = await supabase.rpc('check_email_exists', { p_email: email });
            if (error) throw error;
            if (data) {
                setEmailError('Este correo ya está registrado. ¿Quieres iniciar sesión?');
            } else {
                setEmailError(null);
            }
        } catch (e) {
            console.error('Error checking email:', e);
            setEmailError(null);
        } finally {
            setCheckingEmail(false);
        }
    }, []);

    const handleStep1Submit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.email || !formData.password || !formData.fullName || !formData.gymName) {
            toast({ title: 'Campos requeridos', description: 'Por favor completa todos los campos para continuar.', variant: 'destructive' });
            return;
        }
        if (emailError) {
            toast({ title: 'Correo electrónico', description: emailError, variant: 'destructive' });
            return;
        }
        if (formData.password.length < 6) {
            toast({ title: 'Contraseña muy corta', description: 'La contraseña debe tener al menos 6 caracteres.', variant: 'destructive' });
            return;
        }
        if (!formData.acceptTerms) {
            toast({ title: 'Términos y Condiciones', description: 'Debes aceptar los términos y condiciones para crear tu cuenta.', variant: 'destructive' });
            return;
        }
        setStep(2);
    };

    const handleFinalSubmit = async () => {
        setIsLoading(true);
        try {
            const { data: authData, error: authError } = await supabase.auth.signUp({
                email: formData.email,
                password: formData.password,
                options: {
                    data: {
                        full_name: formData.fullName,
                        role: 'gym_admin'
                    }
                }
            });

            if (authError) {
                if (authError.message?.toLowerCase().includes('already registered') ||
                    authError.message?.toLowerCase().includes('ya registrado')) {
                    toast({
                        title: 'Correo ya registrado',
                        description: 'Este correo ya tiene una cuenta. ¿Quieres iniciar sesión?',
                        variant: 'destructive',
                    });
                    return;
                }
                throw authError;
            }
            if (!authData.user) throw new Error("No se pudo crear el usuario");

            const { error: rpcError } = await supabase.rpc('create_tenant', {
                gym_name: formData.gymName,
                full_name: formData.fullName,
                p_user_id: authData.user.id
            });

            if (rpcError) throw rpcError;

            const { data: profile } = await supabase
                .from('profiles')
                .select('gym_id')
                .eq('id', authData.user.id)
                .single();

            await authApi.acceptTerms(
                authData.user.id,
                profile?.gym_id || null,
                LEGAL_DOCS.terms.version,
                'checkbox_registration'
            );

            if (!profile?.gym_id) {
                throw new Error('No pudimos asociar tu gimnasio a tu cuenta. Inicia sesión con tu correo para continuar.');
            }

            setRegisteredGymName(formData.gymName);
            setIsRegistered(true);

        } catch (error: any) {
            console.error("Registration Error:", error);
            Sentry.captureException(error, {
                tags: { operation: 'gym_registration' },
                extra: { gymName: formData.gymName, email: formData.email }
            });
            toast({
                title: 'Error en el registro',
                description: error.message || 'Ocurrió un error inesperado. Intenta de nuevo.',
                variant: 'destructive',
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleGoToDashboard = async () => {
        await refreshAuth();
        navigate('/dashboard');
    };

    if (isRegistered) {
        return (
            <div className="min-h-screen bg-slate-50 flex flex-col">
                <header className="p-6">
                    <Link to="/" className="flex items-center gap-2 w-fit">
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-600 text-white shadow-lg shadow-orange-600/20">
                            <Dumbbell className="h-6 w-6" />
                        </div>
                        <span className="text-xl font-bold text-slate-900 tracking-tight">GymPro SV</span>
                    </Link>
                </header>
                <div className="flex-1 flex items-center justify-center p-6">
                    <div className="w-full max-w-lg mx-auto text-center space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
                        <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-green-100 mx-auto">
                            <PartyPopper className="h-12 w-12 text-green-600" />
                        </div>
                        <div>
                            <h1 className="text-4xl font-black text-slate-900 tracking-tight mb-3">¡Tu gym está listo!</h1>
                            <p className="text-xl text-slate-500 font-medium">
                                <span className="font-bold text-slate-900">{registredGymName}</span> ha sido creado exitosamente.
                            </p>
                        </div>
                        <Card className="border-none shadow-xl shadow-slate-200/50 rounded-[2rem] overflow-hidden">
                            <CardContent className="p-8 space-y-6">
                                <div className="flex items-center gap-3 justify-center text-sm text-slate-600">
                                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                                    <span>Tu prueba gratuita de <strong>30 días</strong> ha comenzado</span>
                                </div>
                                <div className="bg-green-50 border border-green-200 rounded-2xl p-4 text-left">
                                    <p className="text-xs font-bold text-green-800 uppercase tracking-wider mb-1">Cuenta activa</p>
                                    <p className="text-sm text-green-700">
                                        Tu cuenta ya está activa y tu prueba gratuita ha comenzado. No necesitas confirmar tu correo.
                                    </p>
                                </div>
                                <Button
                                    onClick={handleGoToDashboard}
                                    className="w-full h-16 text-xl font-black bg-orange-600 hover:bg-orange-700 text-white rounded-2xl shadow-xl shadow-orange-600/20 transition-all hover:-translate-y-1"
                                >
                                    Ir a mi dashboard
                                    <ArrowRight className="ml-2 h-6 w-6" />
                                </Button>
                            </CardContent>
                        </Card>
                        <div className="flex items-center justify-center gap-6 text-sm text-slate-400">
                            <span className="flex items-center gap-1.5"><Sparkles className="h-4 w-4" /> Sube tu logo</span>
                            <span className="flex items-center gap-1.5"><User className="h-4 w-4" /> Registra clientes</span>
                            <span className="flex items-center gap-1.5"><Zap className="h-4 w-4" /> Escanea QR</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col">
            <header className="p-6">
                <Link to="/" className="flex items-center gap-2 w-fit">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-600 text-white shadow-lg shadow-orange-600/20">
                        <Dumbbell className="h-6 w-6" />
                    </div>
                    <span className="text-xl font-bold text-slate-900 tracking-tight">GymPro SV</span>
                </Link>
            </header>

            <div className="flex-1 flex items-center justify-center p-6">
                <div className="w-full max-w-5xl grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-8 items-start">
                    <div className="space-y-6">
                        <div className="flex items-center justify-between bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
                            <div className="flex gap-2">
                                {[1, 2].map((s) => (
                                    <div
                                        key={s}
                                        className={`h-2 w-24 rounded-full transition-all duration-500 ${step >= s ? 'bg-orange-600' : 'bg-slate-100'}`}
                                    />
                                ))}
                            </div>
                            <span className="text-xs font-black text-slate-400 uppercase tracking-[0.2em]">Paso {step} de 2</span>
                        </div>

                        {step === 1 && (
                            <form onSubmit={handleStep1Submit} className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                                <div>
                                    <h2 className="text-4xl font-black text-slate-900 tracking-tight mb-2">Crea tu Cuenta</h2>
                                    <p className="text-slate-500 font-medium">Cuéntanos sobre ti y tu gimnasio.</p>
                                </div>

                                {/* Trust bar */}
                                <div className="flex items-center justify-center gap-6 text-xs font-bold text-slate-500 bg-white p-4 rounded-2xl border border-slate-100 shadow-sm">
                                    <span className="flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-green-500" /> 30 días gratis</span>
                                    <span className="w-px h-4 bg-slate-200" />
                                    <span className="flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-green-500" /> Sin tarjeta</span>
                                    <span className="w-px h-4 bg-slate-200" />
                                    <span className="flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-green-500" /> Configuración en 5 min</span>
                                </div>

                                <Card className="border-none shadow-xl shadow-slate-200/50 rounded-[2rem] overflow-hidden">
                                    <CardContent className="p-8 space-y-6">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div className="space-y-2">
                                                <Label className="text-xs font-black uppercase text-slate-400 ml-1">Tu Nombre Completo</Label>
                                                <div className="relative">
                                                    <User className="absolute left-4 top-4 h-5 w-5 text-slate-300" />
                                                    <Input
                                                        name="fullName"
                                                        placeholder="Juan Pérez"
                                                        className="pl-12 h-14 bg-slate-50 border-none focus:ring-2 focus:ring-orange-500/20 rounded-2xl text-slate-900 font-bold text-lg"
                                                        value={formData.fullName}
                                                        onChange={handleInputChange}
                                                        required
                                                    />
                                                </div>
                                            </div>
                                            <div className="space-y-2">
                                                <Label className="text-xs font-black uppercase text-slate-400 ml-1">Nombre del Gimnasio</Label>
                                                <div className="relative">
                                                    <Building className="absolute left-4 top-4 h-5 w-5 text-slate-300" />
                                                    <Input
                                                        name="gymName"
                                                        placeholder="Power Fitness"
                                                        className="pl-12 h-14 bg-slate-50 border-none focus:ring-2 focus:ring-orange-500/20 rounded-2xl text-slate-900 font-bold text-lg"
                                                        value={formData.gymName}
                                                        onChange={handleInputChange}
                                                        required
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-xs font-black uppercase text-slate-400 ml-1">Correo Electrónico</Label>
                                            <div className="relative">
                                                <Mail className="absolute left-4 top-4 h-5 w-5 text-slate-300" />
                                                <Input
                                                    name="email"
                                                    type="email"
                                                    placeholder="admin@gym.com"
                                                    className={`pl-12 h-14 bg-slate-50 border-none focus:ring-2 focus:ring-orange-500/20 rounded-2xl text-slate-900 font-bold text-lg ${emailError ? 'ring-2 ring-red-400 bg-red-50' : ''}`}
                                                    value={formData.email}
                                                    onChange={handleInputChange}
                                                    onBlur={(e) => checkEmailExists(e.target.value)}
                                                    required
                                                />
                                                {checkingEmail && (
                                                    <Loader2 className="absolute right-4 top-4 h-5 w-5 text-slate-400 animate-spin" />
                                                )}
                                            </div>
                                            {emailError && (
                                                <div className="flex items-center gap-2 p-3 bg-red-50 rounded-xl border border-red-200">
                                                    <p className="text-xs font-medium text-red-700">{emailError}</p>
                                                    <Link to="/login" className="text-xs font-bold text-red-700 underline hover:no-underline ml-auto whitespace-nowrap">
                                                        Iniciar sesión
                                                        <ExternalLink className="h-3 w-3 inline ml-1" />
                                                    </Link>
                                                </div>
                                            )}
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-xs font-black uppercase text-slate-400 ml-1">Contraseña</Label>
                                            <div className="relative">
                                                <Lock className="absolute left-4 top-4 h-5 w-5 text-slate-300" />
                                                <Input
                                                    name="password"
                                                    type="password"
                                                    placeholder="••••••••"
                                                    className="pl-12 h-14 bg-slate-50 border-none focus:ring-2 focus:ring-orange-500/20 rounded-2xl text-slate-900 font-bold text-lg"
                                                    value={formData.password}
                                                    onChange={handleInputChange}
                                                    required
                                                    minLength={6}
                                                />
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-3 p-4 bg-orange-50/50 rounded-2xl border border-orange-100/50">
                                            <Checkbox
                                                id="terms"
                                                checked={formData.acceptTerms}
                                                onCheckedChange={(checked) => setFormData({ ...formData, acceptTerms: !!checked })}
                                                className="h-5 w-5 border-2 border-orange-200 data-[state=checked]:bg-orange-600 data-[state=checked]:border-orange-600"
                                            />
                                            <Label htmlFor="terms" className="text-sm font-bold text-slate-900 cursor-pointer leading-tight">
                                                He leído y acepto los{' '}
                                                <button
                                                    type="button"
                                                    onClick={() => setShowTermsModal(true)}
                                                    className="text-orange-600 font-extrabold hover:underline"
                                                >
                                                    términos y condiciones
                                                </button>
                                            </Label>
                                        </div>
                                    </CardContent>
                                </Card>
                                <Button type="submit" className="w-full h-16 text-xl font-black bg-orange-600 hover:bg-orange-700 text-white rounded-2xl shadow-xl shadow-orange-600/20">CONTINUAR</Button>
                            </form>
                        )}

                        {step === 2 && (
                            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                                <div>
                                    <h2 className="text-4xl font-black text-slate-900 tracking-tight mb-2">Revisión Final</h2>
                                    <p className="text-slate-500 font-medium">Confirma los detalles de tu nueva suscripción.</p>
                                </div>

                                <div className="bg-white rounded-[2.5rem] border-2 border-slate-100 p-8 shadow-xl shadow-slate-200/40 space-y-8">
                                    <div className="space-y-4">
                                        <div className="flex items-center gap-4 bg-orange-50 p-6 rounded-3xl border border-orange-100">
                                            <div className="h-12 w-12 bg-orange-600 rounded-2xl flex items-center justify-center text-white">
                                                <CheckCircle2 className="h-6 w-6" />
                                            </div>
                                            <div>
                                                <h4 className="text-xs font-black text-orange-600 uppercase tracking-widest leading-none mb-1">Plan Incluido en tu Prueba</h4>
                                                <p className="text-2xl font-black text-slate-900">{eliteTrialName}</p>
                                                <p className="text-xs font-bold text-slate-500 mt-1">Todo incluido (excepto WhatsApp)</p>
                                            </div>
                                            <div className="ml-auto text-right">
                                                <p className="text-2xl font-black text-slate-900">${eliteTrialPrice}</p>
                                                <p className="text-[10px] font-bold text-slate-400 uppercase">AL MES · 30 DÍAS GRATIS</p>
                                            </div>
                                        </div>

                                        <div className="bg-slate-50 p-6 rounded-3xl border border-slate-100">
                                            <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Funciones incluidas</h4>
                                            <ul className="grid grid-cols-2 gap-2">
                                                {eliteTrialFeatures.map((f, i) => (
                                                    <li key={i} className="flex items-center gap-2 text-sm text-slate-600 font-medium leading-tight">
                                                        <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                                                        {f}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="bg-slate-50 p-6 rounded-3xl border border-slate-100">
                                                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Gimnasio</h4>
                                                <p className="font-bold text-slate-900">{formData.gymName}</p>
                                            </div>
                                            <div className="bg-slate-50 p-6 rounded-3xl border border-slate-100">
                                                <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Administrador</h4>
                                                <p className="font-bold text-slate-900">{formData.fullName}</p>
                                            </div>
                                        </div>

                                        <div className="p-6 bg-indigo-50/50 rounded-3xl border border-indigo-100/50">
                                            <div className="flex items-center gap-2 mb-4">
                                                <Zap className="h-4 w-4 text-indigo-600" />
                                                <h4 className="text-[10px] font-black text-indigo-600 uppercase tracking-widest">Periodo de Prueba</h4>
                                            </div>
                                            <p className="text-sm text-slate-600 font-medium leading-relaxed">
                                                Tu prueba gratuita de <strong>30 días</strong> comienza hoy e incluye el plan <strong>{eliteTrialName}</strong> completo.
                                                Al finalizar la prueba, podrás continuar usando el mismo plan realizando tu primer pago.
                                                No se realizará ningún cargo hoy.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex gap-4">
                                        <Button variant="outline" onClick={() => setStep(1)} className="h-16 px-8 rounded-2xl font-bold text-slate-500">VOLVER</Button>
                                        <Button
                                            disabled={isLoading}
                                            onClick={handleFinalSubmit}
                                            className="flex-1 h-16 text-xl font-black bg-slate-900 hover:bg-black text-white rounded-2xl shadow-xl shadow-slate-900/20"
                                        >
                                            {isLoading ? (
                                                <span className="flex items-center gap-2">
                                                    <Loader2 className="h-6 w-6 animate-spin" />
                                                    CONFIGURANDO...
                                                </span>
                                            ) : (
                                                'EMPEZAR MI PRUEBA GRATIS'
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="hidden lg:block space-y-8 sticky top-6">
                        <div className="p-8 rounded-[2.5rem] bg-slate-900 text-white relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-orange-600/20 blur-[50px] rounded-full" />
                            <h3 className="text-2xl font-black mb-6 relative z-10">¿Por qué elegir <br /> GymPro SV?</h3>
                            <div className="space-y-6 relative z-10">
                                {[
                                    { icon: ShieldCheck, title: 'SaaS Seguro', desc: 'Infraestructura robusta y privada.' },
                                    { icon: Zap, title: 'Implementación Veloz', desc: 'Listo para usar en minutos.' },
                                    { icon: Rocket, title: 'Soporte Local', desc: 'Atención personalizada en El Salvador.' },
                                ].map((item, i) => (
                                    <div key={i} className="flex gap-4">
                                        <div className="h-10 w-10 rounded-xl bg-white/10 flex items-center justify-center text-orange-500">
                                            <item.icon className="h-5 w-5" />
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-sm">{item.title}</h4>
                                            <p className="text-xs text-slate-400 leading-tight">{item.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Dialog open={showTermsModal} onOpenChange={setShowTermsModal}>
                <DialogContent className="sm:max-w-[600px] max-h-[85vh] flex flex-col p-0 overflow-hidden border-none shadow-2xl glass">
                    <div className="p-6 pb-0 shrink-0">
                        <DialogHeader className="flex flex-row items-center gap-4 space-y-0">
                            <div className="h-12 w-12 rounded-2xl bg-orange-100 flex items-center justify-center shrink-0">
                                <ShieldCheck className="h-6 w-6 text-orange-600" />
                            </div>
                            <div>
                                <DialogTitle className="text-xl font-black">Términos y Condiciones</DialogTitle>
                                <DialogDescription className="text-sm font-medium">
                                    Información legal para administradores de GymPro SV.
                                </DialogDescription>
                            </div>
                        </DialogHeader>
                    </div>

                    <div className="flex-1 overflow-y-auto p-6 min-h-0 scrollbar-thin scrollbar-thumb-orange-500/20 scrollbar-track-transparent">
                        <div className="pb-10">
                            <TermsContent variant="modal" />
                        </div>
                    </div>

                    <DialogFooter className="p-6 bg-slate-50 border-t border-slate-100">
                        <Button
                            className="w-full font-bold bg-slate-900 text-white h-12 rounded-xl"
                            onClick={() => {
                                setFormData({ ...formData, acceptTerms: true });
                                setShowTermsModal(false);
                            }}
                        >
                            ENTENDIDO Y ACEPTAR
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
